import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { useLang } from '../i18n';
import { API_BASE } from '../api';
import { getOpeningGMs, getOpeningPhrase } from '../data/openingGMs';
import TimePeriodFilter from './TimePeriodFilter';


// ─── Win rate bar ───
function WinRateBar({ wins, losses, draws, total, c }) {
  const w = (wins / total) * 100;
  const l = (losses / total) * 100;
  const d = Math.max(0, 100 - w - l);
  const labelSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '1rem',
    fontWeight: 900,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  };
  return (
    <Box sx={{ width: '100%', position: 'relative', height: 28 }}>
      <Box sx={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '100%' }}>
        <Box sx={{ width: `${w}%`, backgroundColor: c.blue, transition: 'width 0.6s ease' }} />
        <Box sx={{ width: `${d}%`, backgroundColor: `${c.textMuted}25`, transition: 'width 0.6s ease' }} />
        <Box sx={{ width: `${l}%`, backgroundColor: '#E4446E', transition: 'width 0.6s ease' }} />
      </Box>
      {wins > 0 && (
        <Typography sx={{ ...labelSx, left: `${w / 2}%`, color: c.textDark }}>{wins}W</Typography>
      )}
      {draws > 0 && (
        <Typography sx={{ ...labelSx, left: `${w + d / 2}%`, color: c.textDark }}>{draws}D</Typography>
      )}
      {losses > 0 && (
        <Typography sx={{ ...labelSx, left: `${w + d + l / 2}%`, color: c.textDark }}>{losses}L</Typography>
      )}
    </Box>
  );
}

// ─── Opening row (expandable) ───
function OpeningRow({ opening, c, cardSx, borderColor, username, navigate }) {
  const [expanded, setExpanded] = React.useState(false);
  const wrColor = opening.win_rate >= 0.55 ? c.blue : opening.win_rate < 0.4 ? '#E4446E' : c.textMuted;
  const avgMoves = opening.avg_game_length ? Math.round(opening.avg_game_length / 2) : null;

  return (
    <Box sx={{
      ...cardSx, display: 'flex', flexDirection: 'column', position: 'relative',
      ...(borderColor ? { borderLeft: `3px solid ${borderColor}` } : {}),
    }}>
        {/* Main card (clickable to expand) */}
        <Box onClick={() => setExpanded(!expanded)} sx={{ p: 3, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>
                {opening.opening}
              </Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>
                {opening.total_games} games
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ bgcolor: `${wrColor}15`, borderRadius: '10px', px: 1.5, py: 0.4 }}>
                <Typography sx={{ color: wrColor, fontSize: '1.05rem', fontWeight: 700 }}>
                  {(opening.win_rate * 100).toFixed(0)}%
                </Typography>
              </Box>
              <Box sx={{ color: c.textMuted, transition: 'transform 0.3s ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Box>
            </Box>
          </Box>
          <WinRateBar wins={opening.wins} losses={opening.losses} draws={opening.draws} total={opening.total_games} c={c} />
          {opening.avg_opponent_elo && (
            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', textAlign: 'right' }}>
              Avg opp: {opening.avg_opponent_elo}
            </Typography>
          )}
        </Box>

        {/* Expanded details */}
        {expanded && (
          <Box sx={{ px: 3, pb: 3, borderTop: `1px solid ${c.textMuted}10`, pt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: `${c.textMuted}06` }}>
                <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 800 }}>{(opening.win_rate * 100).toFixed(0)}%</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Win Rate</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: `${c.textMuted}06` }}>
                <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 800 }}>{opening.total_games}</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Games</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: `${c.textMuted}06` }}>
                <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 800 }}>{avgMoves || '—'}</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Avg Moves</Typography>
              </Box>
            </Box>
            {opening.avg_opponent_elo && (
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 2, lineHeight: 1.6 }}>
                Average opponent Elo in this opening: <strong style={{ color: c.textDark }}>{opening.avg_opponent_elo}</strong>
              </Typography>
            )}
            {navigate && (
              <Box
                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/training?opening=${encodeURIComponent(opening.opening)}&username=${username}`); }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2,
                  py: 1.2, px: 1.5, borderRadius: '12px', cursor: 'pointer',
                  bgcolor: `#8F7BE012`, border: `1px solid #8F7BE020`,
                  transition: 'all 0.2s ease', flexWrap: 'wrap',
                  '&:hover': { bgcolor: '#8F7BE020', borderColor: '#8F7BE040' },
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8F7BE0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="12" y1="3" x2="12" y2="21" />
                </svg>
                <Typography sx={{ color: '#8F7BE0', fontSize: '1.05rem', fontWeight: 700 }}>
                  Train this opening
                  <Box component="span" sx={{ fontWeight: 500, opacity: 0.8, ml: 0.8 }}>— personalized puzzles from your games</Box>
                </Typography>
              </Box>
            )}
          </Box>
        )}
    </Box>
  );
}

// ─── Cascade animation ───
function CascadeIn({ children }) {
  return <>{children}</>;
}

// ═══════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════
export default function OpeningsPage() {
  const { c } = useDestrierTheme();
  const { t } = useLang();
  const cardSx = getCardSx(c);
  const cache = useDataCache();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('fetching');
  const [cascadeReady, setCascadeReady] = useState(false);
  const [, setError] = useState(null);
  const [scatterIdx, setScatterIdx] = useState(0);
  const [scatterColorFilter, setScatterColorFilter] = useState('all');

  useEffect(() => {
    if (!username) return;
    const ck = `openings:${username}:${period}`;
    const hit = cache.get(ck);
    if (hit) { setData(hit); setPhase('dashboard'); setTimeout(() => setCascadeReady(true), 80); return; }
    setError(null);
    setPhase('fetching');
    let cancelled = false;
    fetch(`${API_BASE}/openings?username=${username}&period=${period}`)
      .then((r) => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then((d) => { if (!cancelled) { cache.set(ck, d); setData(d); setPhase('dashboard'); setTimeout(() => setCascadeReady(true), 80); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!username) {
    return (
      <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography sx={{ color: c.textMuted }}>{t.pleaseProvideUsername}</Typography>
      </Box>
    );
  }

  const d = data;

  // Strip "Unknown" openings from every list so they never surface on the page.
  const isKnown = (o) => o?.opening && !o.opening.toLowerCase().includes('unknown');
  const knownWhite = (d?.white_openings || []).filter(isKnown);
  const knownBlack = (d?.black_openings || []).filter(isKnown);
  const knownUnderperforming = (d?.underperforming || []).filter(isKnown);
  const knownBest = (d?.best_openings || []).filter(isKnown);
  const knownRecommendations = (d?.recommendations || []).filter(
    r => r && isKnown({ opening: r.opening }) && (!r.suggestion || !r.suggestion.toLowerCase().includes('unknown'))
  );

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      {phase === 'fetching' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      )}

      {phase === 'dashboard' && d && (
        <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pb: 6, pt: { xs: 9, md: 10 } }}>
          <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <TimePeriodFilter value={period} onChange={setPeriod} />
            </Box>
          </Box>

          {/* Title */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              {t.openingRepertoire}
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              Destrier analyzed your <strong style={{ color: '#8A74E4' }}>opening repertoire</strong> across <strong style={{ color: c.textDark }}>{d.total_games_analyzed.toLocaleString()} games</strong>.
              Identify which openings are working, which are costing you wins, and where to try something new.
            </Typography>
          </Box>

          {/* Most played openings banner */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            {knownWhite[0] && (() => {
              const wo = knownWhite[0];
              const wPhrase = getOpeningPhrase(wo.opening);
              return (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 3 } }}>
                  <Box sx={{ ...cardSx, p: 3, flexShrink: 0, minWidth: 280, border: `1.5px solid ${c.textMuted}15` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#ECE2DB', border: `1.5px solid ${c.textMuted}30`, flexShrink: 0 }} />
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Favorite as White
                      </Typography>
                    </Box>
                    <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 900, lineHeight: 1.2 }}>
                      {wo.opening}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
                      Played <strong style={{ color: c.textDark }}>{wo.total_games} times</strong> — <strong style={{ color: wo.win_rate >= 0.5 ? '#3DB5D4' : '#E4446E' }}>{Math.round(wo.win_rate * 100)}% WR</strong> — {wo.wins}W {wo.draws}D {wo.losses}L
                    </Typography>
                    {wPhrase && (
                      <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mt: 0.3, fontStyle: 'italic' }}>
                        {wPhrase}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })()}
            {knownBlack[0] && (() => {
              const bo = knownBlack[0];
              const bPhrase = getOpeningPhrase(bo.opening);
              return (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 3 } }}>
                  <Box sx={{ ...cardSx, p: 3, flexShrink: 0, minWidth: 280, border: `1.5px solid ${c.textMuted}15` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#111115', flexShrink: 0 }} />
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Favorite as Black
                      </Typography>
                    </Box>
                    <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 900, lineHeight: 1.2 }}>
                      {bo.opening}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
                      Played <strong style={{ color: c.textDark }}>{bo.total_games} times</strong> — <strong style={{ color: bo.win_rate >= 0.5 ? '#3DB5D4' : '#E4446E' }}>{Math.round(bo.win_rate * 100)}% WR</strong> — {bo.wins}W {bo.draws}D {bo.losses}L
                    </Typography>
                    {bPhrase && (
                      <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mt: 0.3, fontStyle: 'italic' }}>
                        {bPhrase}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </Box>

          {/* Opening Performance Map — Scatter Plot */}
          <CascadeIn show={cascadeReady} delay={0.15}>
            {(() => {
              const baseOpenings = [...knownWhite.map(o => ({ ...o, color: 'white' })), ...knownBlack.map(o => ({ ...o, color: 'black' }))];
              // Adaptive games threshold: prefer >=3 but relax down to 1 when data is sparse
              // (e.g. a 1-week period with only a handful of games) so the scatter always renders.
              const DESIRED_MIN_POINTS = 4;
              const adaptiveMinGames = [3, 2, 1].find(t => baseOpenings.filter(o => o.total_games >= t).length >= DESIRED_MIN_POINTS) ?? 1;
              const allOpenings = baseOpenings.filter(o => o.total_games >= adaptiveMinGames);
              if (allOpenings.length === 0) return null;

              const filtered = scatterColorFilter === 'all' ? allOpenings : allOpenings.filter(o => o.color === scatterColorFilter);
              // Top 5 by games: these get permanent labels; others only on hover/select
              const PINNED_COUNT = 5;
              const pinnedIdxSet = new Set(
                filtered
                  .map((o, i) => ({ i, games: o.total_games }))
                  .sort((a, b) => b.games - a.games)
                  .slice(0, PINNED_COUNT)
                  .map(x => x.i)
              );

              // Compute axis ranges with margin so nothing sits on edges
              const dataMaxGames = Math.max(...filtered.map(o => o.total_games), 1);
              const dataMinGames = Math.min(...filtered.map(o => o.total_games), 0);
              const dataMaxWR = Math.max(...filtered.map(o => o.win_rate), 0.5);
              const dataMinWR = Math.min(...filtered.map(o => o.win_rate), 0.5);
              const gamesPad = Math.max(1, Math.ceil(dataMaxGames * 0.1));
              const wrPad = 0.05;
              const xMin = Math.max(0, dataMinGames - gamesPad);
              const xMax = dataMaxGames + gamesPad;
              const yMin = Math.max(0, Math.floor((dataMinWR - wrPad) * 20) / 20);
              const yMax = Math.min(1, Math.ceil((dataMaxWR + wrPad) * 20) / 20);

              const W = 600, H = 420, padL = 58, padR = 20, padT = 30, padB = 54;
              const chartW = W - padL - padR;
              const chartH = H - padT - padB;
              const xOf = (games) => padL + ((games - xMin) / (xMax - xMin)) * chartW;
              const yOf = (wr) => padT + (1 - (wr - yMin) / (yMax - yMin)) * chartH;
              // Continuous gradient: min WR = red, 50% WR = cream, max WR = blue
              const wrRange = dataMaxWR - dataMinWR || 1;
              const getDotColor = (wr) => {
                const t = Math.max(0, Math.min(1, (wr - dataMinWR) / wrRange));
                const r1 = 228, g1 = 68, b1 = 110;    // #E4446E (red)
                const rm = 236, gm = 226, bm = 219;   // #ECE2DB (cream)
                const r2 = 61, g2 = 181, b2 = 212;    // #3DB5D4 (blue)
                let r, g, b;
                if (t <= 0.5) {
                  const s = t / 0.5;
                  r = Math.round(r1 + (rm - r1) * s);
                  g = Math.round(g1 + (gm - g1) * s);
                  b = Math.round(b1 + (bm - b1) * s);
                } else {
                  const s = (t - 0.5) / 0.5;
                  r = Math.round(rm + (r2 - rm) * s);
                  g = Math.round(gm + (g2 - gm) * s);
                  b = Math.round(bm + (b2 - bm) * s);
                }
                return `rgb(${r},${g},${b})`;
              };

              // Y ticks: nice steps within range
              const yTicks = [];
              const yStep = 0.1;
              for (let v = Math.ceil(yMin / yStep) * yStep; v <= yMax + 0.001; v += yStep) yTicks.push(Math.round(v * 100) / 100);
              // X ticks
              const xTicks = [];
              const xStep = (xMax - xMin) <= 10 ? 2 : (xMax - xMin) <= 50 ? 5 : (xMax - xMin) <= 100 ? 10 : Math.ceil((xMax - xMin) / 5 / 10) * 10;
              for (let v = Math.ceil(xMin / xStep) * xStep; v <= xMax; v += xStep) xTicks.push(v);

              // Ensure scatterIdx is valid for filtered list
              const selected = scatterIdx !== null && scatterIdx < filtered.length ? filtered[scatterIdx] : filtered[0];
              const selectedIdx = selected ? filtered.indexOf(selected) : 0;
              const selectedColor = selected ? getDotColor(selected.win_rate) : null;

              return (
                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 3, scrollMarginTop: '80px' }}>
                    Opening Performance Map
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                    {/* Scatter chart (left) */}
                    <Box sx={{
                      ...getCardSx(c), flex: 2, minWidth: 0, p: 3,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      {/* Color filter tabs */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Win Rate vs Games Played</div>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                            Select your color
                          </Typography>
                          <Box sx={{ display: 'flex', bgcolor: `${c.textMuted}10`, borderRadius: '10px', p: 0.3 }}>
                          {[{ key: 'all', label: 'All' }, { key: 'white', label: 'White' }, { key: 'black', label: 'Black' }].map(({ key, label }) => (
                            <Box key={key} onClick={() => { setScatterColorFilter(key); setScatterIdx(0); }}
                              sx={{
                                px: 1.5, py: 0.4, borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 0.6,
                                fontSize: '1.05rem', fontWeight: scatterColorFilter === key ? 700 : 500,
                                color: scatterColorFilter === key ? c.textDark : c.textMuted,
                                bgcolor: scatterColorFilter === key ? c.bg : 'transparent',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {key === 'white' && <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#ECE2DB', border: `1.5px solid ${c.textMuted}30`, flexShrink: 0 }} />}
                              {key === 'black' && <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#111115', flexShrink: 0 }} />}
                              {label}
                            </Box>
                          ))}
                          </Box>
                        </Box>
                      </Box>
                      <style>{`
                        @keyframes scatterDotPop {
                          from { transform: scale(0); opacity: 0; }
                          to { transform: scale(1); opacity: 1; }
                        }
                        @keyframes scatterGridDraw {
                          from { opacity: 0; }
                          to { opacity: 1; }
                        }
                        .scatter-grid {
                          opacity: 0;
                          animation: scatterGridDraw 0.6s ease forwards 0.2s;
                        }
                        .scatter-dot {
                          transform-origin: center;
                          animation: scatterDotPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                          opacity: 0;
                        }
                        .scatter-zone-label {
                          opacity: 0;
                          animation: scatterZoneFade 0.5s ease forwards;
                        }
                        @keyframes scatterZoneFade {
                          from { opacity: 0; }
                          to { opacity: 0.5; }
                        }
                      `}</style>
                      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', overflow: 'visible', cursor: 'pointer' }}>
                        {/* Win-rate bands: blue above 50%, red below 50% — split exactly at yOf(0.5) */}
                        {(() => {
                          const midY = Math.max(padT, Math.min(H - padB, yOf(0.5)));
                          const leftX = padL;
                          const rightX = W - padR;
                          const topY = padT;
                          const botY = H - padB;
                          return (
                            <g pointerEvents="none">
                              {midY > topY && (
                                <rect x={leftX} y={topY} width={rightX - leftX} height={midY - topY} fill="#3DB5D4" opacity="0.05" />
                              )}
                              {botY > midY && (
                                <rect x={leftX} y={midY} width={rightX - leftX} height={botY - midY} fill="#E4446E" opacity="0.05" />
                              )}
                            </g>
                          );
                        })()}
                        {/* Grid */}
                        <g className="scatter-grid">
                        {yTicks.filter(v => v > yMin && v < yMax).map(v => (
                          <g key={`y-${v}`}>
                            <line x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)} stroke={c.textMuted} strokeWidth="0.5" opacity="0.15" />
                            <text x={padL - 8} y={yOf(v)} fill={c.textMuted} fontSize="12" fontWeight="600" textAnchor="end" dominantBaseline="middle">{Math.round(v * 100)}%</text>
                          </g>
                        ))}
                        {xTicks.filter(v => v > xMin && v < xMax).map(v => (
                          <g key={`x-${v}`}>
                            <line x1={xOf(v)} y1={padT} x2={xOf(v)} y2={H - padB} stroke={c.textMuted} strokeWidth="0.5" opacity="0.15" />
                            <text x={xOf(v)} y={H - padB + 18} fill={c.textMuted} fontSize="12" fontWeight="600" textAnchor="middle">{v}</text>
                          </g>
                        ))}
                        {/* Axis labels */}
                        <text x={W / 2} y={H - 2} fill={c.textMuted} fontSize="13" fontWeight="600" textAnchor="middle">Games Played</text>
                        <text x={12} y={H / 2} fill={c.textMuted} fontSize="13" fontWeight="600" textAnchor="middle" dominantBaseline="middle" transform={`rotate(-90, 12, ${H / 2})`}>Win Rate</text>
                        </g>
                        {/* Dots with labels — always separated, more on hover */}
                        {(() => {
                          const raw = filtered.map(o => ({ x: xOf(o.total_games), y: yOf(o.win_rate) }));
                          const MIN_DIST = 22;
                          // Always-on separation: iterate a few times to push overlapping dots apart
                          const settled = raw.map(p => ({ ...p }));
                          for (let iter = 0; iter < 5; iter++) {
                            for (let i = 0; i < settled.length; i++) {
                              for (let j = i + 1; j < settled.length; j++) {
                                let dx = settled[j].x - settled[i].x;
                                let dy = settled[j].y - settled[i].y;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                if (dist < MIN_DIST) {
                                  // Push apart equally
                                  const overlap = (MIN_DIST - dist) / 2 + 0.5;
                                  if (dist === 0) { dx = 1; dy = 0; }
                                  const nx = dx / (dist || 1);
                                  const ny = dy / (dist || 1);
                                  settled[i].x -= nx * overlap;
                                  settled[i].y -= ny * overlap;
                                  settled[j].x += nx * overlap;
                                  settled[j].y += ny * overlap;
                                }
                              }
                            }
                          }
                          // Extra repulsion from hovered dot
                          const HOVER_REPEL = 30;
                          const HOVER_FORCE = 20;
                          const rawPositions = settled.map((pos, i) => {
                            if (selectedIdx === null || i === selectedIdx) return pos;
                            const sel = settled[selectedIdx];
                            const dx = pos.x - sel.x;
                            const dy = pos.y - sel.y;
                            const dist = Math.sqrt(dx * dx + dy * dy);
                            if (dist < HOVER_REPEL && dist > 0) {
                              const push = (HOVER_REPEL - dist) / HOVER_REPEL * HOVER_FORCE;
                              return { x: pos.x + (dx / dist) * push, y: pos.y + (dy / dist) * push };
                            }
                            return pos;
                          });
                          // Clamp dots to the chart area so repulsion can't push them past the axes/grid.
                          // Account for the selected-dot ring (r + 6) so nothing visually clips.
                          const CLAMP_PAD = 8 + 6;
                          const positions = rawPositions.map(pos => ({
                            x: Math.max(padL + CLAMP_PAD, Math.min(W - padR - CLAMP_PAD, pos.x)),
                            y: Math.max(padT + CLAMP_PAD, Math.min(H - padB - CLAMP_PAD, pos.y)),
                          }));

                          // Compute label positions with collision avoidance — only for visible labels
                          // (pinned top-N + currently selected). Others are hidden until hovered.
                          const DOT_R = 8;
                          // Extra clearance so the mouse cursor on a hovered dot doesn't sit on the text.
                          const LABEL_ABOVE = DOT_R + 14;
                          const LABEL_BELOW = DOT_R + 20;
                          const labelPositions = positions
                            .map((pos, i) => ({
                              x: pos.x,
                              y: pos.y - LABEL_ABOVE, // default: above dot
                              dotY: pos.y,
                              idx: i,
                            }))
                            .filter(p => pinnedIdxSet.has(p.idx) || p.idx === selectedIdx);
                          // Sort by y for easier vertical deconfliction
                          const sorted = [...labelPositions].sort((a, b) => a.y - b.y);
                          const MIN_LABEL_GAP = 12;
                          for (let pass = 0; pass < 5; pass++) {
                            for (let j = 1; j < sorted.length; j++) {
                              const prev = sorted[j - 1];
                              const curr = sorted[j];
                              if (Math.abs(prev.x - curr.x) < 60) {
                                const gap = curr.y - prev.y;
                                if (gap < MIN_LABEL_GAP) {
                                  const push = (MIN_LABEL_GAP - gap) / 2 + 0.5;
                                  prev.y -= push;
                                  curr.y += push;
                                }
                              }
                            }
                          }
                          // After deconfliction, check if any label overlaps a dot — if so, flip below
                          sorted.forEach(label => {
                            for (let k = 0; k < positions.length; k++) {
                              if (k === label.idx) continue;
                              const dot = positions[k];
                              const dx = Math.abs(label.x - dot.x);
                              const dy = Math.abs(label.y - dot.y);
                              if (dx < 40 && dy < DOT_R + 3) {
                                // Label overlaps another dot — move below its own dot instead
                                label.y = label.dotY + LABEL_BELOW;
                                break;
                              }
                            }
                          });
                          // Final guard: if deconfliction pushed a label onto its OWN dot, flip below.
                          // Keeps the hovered dot's label from landing under the mouse cursor.
                          sorted.forEach(label => {
                            if (Math.abs(label.y - label.dotY) < DOT_R + 6) {
                              label.y = label.dotY + LABEL_BELOW;
                            }
                          });
                          // Map back to index order
                          const labelYByIdx = {};
                          sorted.forEach(l => { labelYByIdx[l.idx] = l.y; });

                          // Two-pass render: all dots first, then all labels on top.
                          // SVG paints in document order, so labels after dots always win z-order.
                          const r = 8;
                          const dotsPass = filtered.map((o, i) => {
                            const dotColor = getDotColor(o.win_rate);
                            const isSelected = i === selectedIdx;
                            const px = positions[i].x;
                            const py = positions[i].y;
                            return (
                              <g key={`dot-${i}`} className="scatter-dot" style={{ animationDelay: `${0.5 + i * 0.06}s`, cursor: 'pointer' }}
                                onMouseEnter={() => setScatterIdx(i)}
                                onClick={() => setScatterIdx(i)}
                              >
                                <circle cx={px} cy={py} r={r + 14} fill="transparent" style={{ transition: 'cx 0.3s ease, cy 0.3s ease' }} />
                                {isSelected && <circle cx={px} cy={py} r={r + 6} fill={dotColor} opacity="0.12" style={{ transition: 'cx 0.3s ease, cy 0.3s ease' }} />}
                                <circle cx={px} cy={py} r={isSelected ? r + 2 : r} fill={dotColor}
                                  style={{ transition: 'cx 0.3s ease, cy 0.3s ease, filter 0.2s', filter: isSelected ? `drop-shadow(0 0 8px ${dotColor}60)` : 'none' }}
                                />
                              </g>
                            );
                          });
                          // Dark halo via paint-order:stroke:fill — stroke draws first, fill on top,
                          // giving legible text over any dot color (red, cream, blue).
                          const haloStyle = {
                            paintOrder: 'stroke',
                            stroke: c.bg,
                            strokeWidth: 4,
                            strokeLinejoin: 'round',
                            strokeLinecap: 'round',
                          };
                          const zoneHaloStyle = { ...haloStyle, strokeWidth: 3 };
                          const zoneLabels = (
                            <g key="zones" style={{ pointerEvents: 'none' }}>
                              <text className="scatter-zone-label" style={{ ...zoneHaloStyle, animationDelay: '1.2s' }} x={padL + 8} y={padT + 16} fill="#3DB5D4" fontSize="11" fontWeight="700">Play more of these</text>
                              <text className="scatter-zone-label" style={{ ...zoneHaloStyle, animationDelay: '1.2s' }} x={W - padR - 8} y={padT + 16} fill="#3DB5D4" fontSize="11" fontWeight="700" textAnchor="end">Keep playing these</text>
                              <text className="scatter-zone-label" style={{ ...zoneHaloStyle, animationDelay: '1.2s' }} x={padL + 8} y={H - padB - 8} fill="#E4446E" fontSize="11" fontWeight="700">Train before playing more</text>
                              <text className="scatter-zone-label" style={{ ...zoneHaloStyle, animationDelay: '1.2s' }} x={W - padR - 8} y={H - padB - 8} fill="#E4446E" fontSize="11" fontWeight="700" textAnchor="end">Focus on training these</text>
                            </g>
                          );
                          const labelsPass = filtered.map((o, i) => {
                            const isSelected = i === selectedIdx;
                            const isPinned = pinnedIdxSet.has(i);
                            const labelVisible = isPinned || isSelected;
                            if (!labelVisible) return null;
                            const px = positions[i].x;
                            const py = positions[i].y;
                            const labelY = labelYByIdx[i] ?? (py - LABEL_ABOVE);
                            // Stats line sits on the far side of the main label from the dot,
                            // so it doesn't overlap the dot when the main label flips below.
                            const labelAbove = labelY < py;
                            const statsY = labelY + (labelAbove ? -13 : 13);
                            return (
                              <g key={`label-${i}`} style={{ pointerEvents: 'none' }}>
                                <text x={px} y={statsY} fill={c.textMuted} fontSize={isSelected ? '11' : '0'} fontWeight="600" textAnchor="middle"
                                  style={{ ...haloStyle, opacity: isSelected ? 1 : 0, transition: 'font-size 0.2s, opacity 0.2s' }}>
                                  {o.total_games} games · {Math.round(o.win_rate * 100)}% WR
                                </text>
                                <text x={px} y={labelY} fill={isSelected ? c.textDark : c.textMuted} fontSize={isSelected ? '12' : '10'} fontWeight={isSelected ? '700' : '600'} textAnchor="middle"
                                  style={{ ...haloStyle, opacity: isSelected ? 1 : 0.7, transition: 'font-size 0.2s, fill 0.2s, opacity 0.3s' }}>
                                  {o.opening}</text>
                              </g>
                            );
                          });
                          return <>{dotsPass}{zoneLabels}{labelsPass}</>;
                        })()}
                      </svg>
                      {/* Legend — gradient bar */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '1.05rem', color: '#E4446E', fontWeight: 600 }}>{Math.round(dataMinWR * 100)}%</Typography>
                        <Box sx={{ width: 120, height: 8, borderRadius: 4, background: 'linear-gradient(90deg, #E4446E, #ECE2DB 50%, #3DB5D4)' }} />
                        <Typography sx={{ fontSize: '1.05rem', color: '#3DB5D4', fontWeight: 600 }}>{Math.round(dataMaxWR * 100)}%</Typography>
                        <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, ml: 0.5 }}>Win Rate</Typography>
                      </Box>
                    </Box>

                    {/* Detail panel (right) — vertical bar + info */}
                    <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'row', gap: 4, alignSelf: 'stretch' }}>
                      {/* Vertical W/D/L bar — full height */}
                      {selected && (() => {
                        const o = selected;
                        const winPct = Math.round(o.wins / o.total_games * 100);
                        const drawPct = Math.round(o.draws / o.total_games * 100);
                        const lossPct = 100 - winPct - drawPct;
                        return (
                          <Box sx={{ width: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '1.05rem', color: c.blue, fontWeight: 700, mb: 0.5 }}>{winPct}%</Typography>
                            <Box sx={{ flex: 1, width: '100%', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ width: '100%', height: `${winPct}%`, bgcolor: c.blue, opacity: 0.7, transition: 'height 0.6s ease' }} />
                              <Box sx={{ width: '100%', height: `${drawPct}%`, bgcolor: `${c.textMuted}30`, transition: 'height 0.6s ease' }} />
                              <Box sx={{ width: '100%', height: `${lossPct}%`, bgcolor: '#E4446E', opacity: 0.7, transition: 'height 0.6s ease' }} />
                            </Box>
                            <Typography sx={{ fontSize: '1.05rem', color: '#E4446E', fontWeight: 700, mt: 0.5 }}>{lossPct}%</Typography>
                          </Box>
                        );
                      })()}

                      {/* Info */}
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {selected && (() => {
                          const o = selected;
                          const col = selectedColor;
                          const gms = getOpeningGMs(o.opening);
                          const phrase = getOpeningPhrase(o.opening);
                          const recText = o.win_rate >= (yMin + yMax) / 2
                            ? (o.total_games >= (xMin + xMax) / 2 ? 'Keep playing' : 'Play more')
                            : (o.total_games >= (xMin + xMax) / 2 ? 'Focus on training' : 'Train before playing more');
                          const recColor = o.win_rate >= (yMin + yMax) / 2 ? '#3DB5D4' : '#E4446E';
                          return (
                            <>
                              {/* 1. Opening Details */}
                              <div style={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Opening Details</div>
                              <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 800, mb: 0.3 }}>{o.opening}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: o.color === 'white' ? '#fff' : '#333', border: `1px solid ${c.textMuted}40` }} />
                                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600 }}>Playing as {o.color}</Typography>
                              </Box>

                              {/* 2. Famous for this opening */}
                              {(gms.length > 0 || phrase) && (
                                <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${c.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                                  {gms.length > 0 && (
                                    <>
                                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Famous for this opening</Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {gms.map(gm => (
                                          <Box key={gm} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.5, borderRadius: '10px', bgcolor: `${c.textMuted}08`, border: `1px solid ${c.textMuted}12` }}>
                                            <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>{gm}</Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </>
                                  )}
                                  {phrase && (
                                    <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mt: 1, fontStyle: 'italic' }}>
                                      {phrase}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* 3. Your Statistics */}
                              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>Your Statistics</Typography>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 10, columnGap: 16, marginBottom: 16 }}>
                                {[
                                  { label: 'Games', value: o.total_games, big: true },
                                  { label: 'Win Rate', value: `${Math.round(o.win_rate * 100)}%`, color: col, big: true },
                                  { label: 'Record', value: `${o.wins}W - ${o.draws}D - ${o.losses}L` },
                                  o.avg_opponent_elo ? { label: 'Avg Opponent', value: Math.round(o.avg_opponent_elo) } : null,
                                  { label: 'Draw Rate', value: `${Math.round(o.draws / o.total_games * 100)}%` },
                                  { label: 'Win/Loss Ratio', value: o.losses > 0 ? `${(o.wins / o.losses).toFixed(1)}:1` : '\u221e:1' },
                                  { label: 'Win Streak', value: o.win_rate >= 0.6 ? 'High' : o.win_rate >= 0.45 ? 'Medium' : 'Low' },
                                  { label: 'Experience', value: o.total_games >= 20 ? 'Well-practiced' : o.total_games >= 8 ? 'Familiar' : 'Exploring' },
                                ].filter(Boolean).map(s => (
                                  <div key={s.label}>
                                    <div style={{ color: c.textMuted, fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.2, margin: 0 }}>{s.label}</div>
                                    <div style={{ color: s.color || c.textDark, fontSize: s.big ? '1.2rem' : '1.05rem', fontWeight: s.big ? 900 : 700, lineHeight: 1.3, margin: 0 }}>{s.value}</div>
                                  </div>
                                ))}
                              </div>

                              {/* 4. Recommendation + Train button */}
                              <Box sx={{ pt: 2, borderTop: `1px solid ${c.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Recommendation</Typography>
                                <Typography sx={{ color: recColor, fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
                                  {recText}
                                </Typography>
                                <Box
                                  onClick={() => navigate(`/dashboard/training?opening=${encodeURIComponent(o.opening)}`)}
                                  sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 1,
                                    px: 2.5, py: 1.2, borderRadius: '12px', cursor: 'pointer',
                                    bgcolor: '#8A74E4', color: '#fff', fontWeight: 700, fontSize: '1.05rem',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { bgcolor: '#7A64D4' },
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="12" y1="3" x2="12" y2="21" />
                                  </svg>
                                  Train this opening in the Opening Trainer
                                </Box>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </CascadeIn>


          {/* Most Faced Openings */}
          {(knownWhite.length > 0 || knownBlack.length > 0) && (
            <CascadeIn show={cascadeReady} delay={0.4}>
              <Box id="most-faced" sx={{ mt: 6, mb: 5 }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                  Most Played Openings
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                  The openings you encounter most frequently. Knowing these helps you prepare targeted responses.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  {/* As White */}
                  {knownWhite.length > 0 && (
                    <Box sx={{ ...cardSx }}>
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#ECE2DB', border: `1.5px solid ${c.textMuted}30`, flexShrink: 0 }} />
                          <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>As White</Typography>
                        </Box>
                        {knownWhite.slice(0, 5).map((o, i) => {
                          const wr = Math.round(o.win_rate * 100);
                          const wrColor = wr >= 55 ? c.blue : wr < 45 ? '#E4446E' : c.textMuted;
                          const maxGames = knownWhite[0]?.total_games || 1;
                          return (
                            <Box key={i} sx={{ mb: i < 4 ? 2 : 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600 }}>{o.opening}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{o.total_games} games</Typography>
                                  <Box sx={{ bgcolor: `${wrColor}15`, borderRadius: '8px', px: 1.2, py: 0.2 }}>
                                    <Typography sx={{ color: wrColor, fontSize: '1.05rem', fontWeight: 700 }}>{wr}%</Typography>
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ height: 6, borderRadius: 3, bgcolor: `${c.textMuted}10`, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${(o.total_games / maxGames) * 100}%`, borderRadius: 3, bgcolor: `${c.blue}60`, transition: 'width 0.6s ease' }} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {/* As Black */}
                  {knownBlack.length > 0 && (
                    <Box sx={{ ...cardSx }}>
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#111115', flexShrink: 0 }} />
                          <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>As Black</Typography>
                        </Box>
                        {knownBlack.slice(0, 5).map((o, i) => {
                          const wr = Math.round(o.win_rate * 100);
                          const wrColor = wr >= 55 ? c.blue : wr < 45 ? '#E4446E' : c.textMuted;
                          const maxGames = knownBlack[0]?.total_games || 1;
                          return (
                            <Box key={i} sx={{ mb: i < 4 ? 2 : 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600 }}>{o.opening}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{o.total_games} games</Typography>
                                  <Box sx={{ bgcolor: `${wrColor}15`, borderRadius: '8px', px: 1.2, py: 0.2 }}>
                                    <Typography sx={{ color: wrColor, fontSize: '1.05rem', fontWeight: 700 }}>{wr}%</Typography>
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ height: 6, borderRadius: 3, bgcolor: `${c.textMuted}10`, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${(o.total_games / maxGames) * 100}%`, borderRadius: 3, bgcolor: `#8A74E460`, transition: 'width 0.6s ease' }} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </CascadeIn>
          )}

          {/* Underperforming */}
          {knownUnderperforming.length > 0 && (
            <CascadeIn show={cascadeReady} delay={0.5}>
              <Box id="underperforming" sx={{ mt: 6, mb: 5 }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                  {t.underperformingOpenings}
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                  Your weakest openings by win rate — candidates to rework or replace.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {knownUnderperforming.slice(0, 6).map((opening) => (
                    <OpeningRow
                      key={opening.opening}
                      opening={opening}
                      c={c}
                      cardSx={cardSx}
                      username={username}
                      navigate={navigate}
                    />
                  ))}
                </Box>
              </Box>
            </CascadeIn>
          )}

          {/* Strongest openings — mirror of underperforming */}
          {knownBest.length > 0 && (
            <CascadeIn show={cascadeReady} delay={0.55}>
              <Box id="strongest" sx={{ mt: 6, mb: 5 }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                  Strongest Openings
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                  Your highest win-rate openings — lean on these when you want a reliable edge.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {knownBest.slice(0, 6).map((opening) => (
                    <OpeningRow
                      key={opening.opening}
                      opening={opening}
                      c={c}
                      cardSx={cardSx}
                      username={username}
                      navigate={navigate}
                    />
                  ))}
                </Box>
              </Box>
            </CascadeIn>
          )}

          {/* Recommendations — consolidated, grouped by suggested opening */}
          {knownRecommendations.length > 0 && (() => {
            const bySuggestion = {};
            knownRecommendations.filter(r => r.suggestion).forEach(r => {
              const impact = Math.round((r.suggestion_win_rate - r.current_win_rate) * r.games_played * 0.5);
              if (!bySuggestion[r.suggestion]) {
                bySuggestion[r.suggestion] = {
                  suggestion: r.suggestion,
                  suggestion_win_rate: r.suggestion_win_rate,
                  suggestion_games: r.suggestion_games,
                  replaces: [],
                  totalImpact: 0,
                };
              }
              bySuggestion[r.suggestion].replaces.push({
                opening: r.opening,
                current_win_rate: r.current_win_rate,
                games_played: r.games_played,
              });
              bySuggestion[r.suggestion].totalImpact += Math.max(0, impact);
            });
            const grouped = Object.values(bySuggestion).sort((a, b) => b.totalImpact - a.totalImpact);
            if (grouped.length === 0) return null;
            return (
              <CascadeIn show={cascadeReady} delay={0.6}>
                <Box id="recommendations" sx={{ mt: 6, mb: 5 }}>
                  <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                    Recommendations
                  </Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                    Openings you already play well, grouped by the weaker lines they could replace.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {grouped.map((g) => (
                      <Box key={g.suggestion} sx={{
                        ...cardSx,
                        p: 2.5,
                        display: 'flex', flexDirection: 'column', gap: 1.2,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ color: c.blue, fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Try
                          </Typography>
                          <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 800 }}>
                            {g.suggestion}
                          </Typography>
                          {g.totalImpact > 0 && (
                            <Box sx={{ ml: 'auto', bgcolor: '#00A5C420', borderRadius: '8px', px: 1.1, py: 0.2 }}>
                              <Typography sx={{ color: '#00A5C4', fontSize: '1rem', fontWeight: 700 }}>+~{g.totalImpact} elo</Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>
                          {(g.suggestion_win_rate * 100).toFixed(0)}% WR over {g.suggestion_games} games
                        </Typography>
                        <Box sx={{ borderTop: `1px solid ${c.textMuted}18`, pt: 1.2, mt: 0.3 }}>
                          <Typography sx={{ color: c.textMuted, fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.8 }}>
                            Instead of
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {g.replaces.map(rep => (
                              <Box key={rep.opening} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, flex: 1, minWidth: 0 }}>
                                  {rep.opening}
                                </Typography>
                                <Typography sx={{ color: '#E4446E', fontSize: '1.05rem', fontWeight: 600, flexShrink: 0 }}>
                                  {(rep.current_win_rate * 100).toFixed(0)}% · {rep.games_played} games
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CascadeIn>
            );
          })()}

        </Box>
      )}
    </Box>
  );
}
